import type { NuxtApp } from '#app';
import typeOf from 'just-typeof';

const LOCALE_UPDATE_PRIORITIES = {
    platform: 10,
    route: 20,
    user: 30,
};

/**
 * Get the platform locale from the API
 *
 * @returns {Promise<string | null>} The platform locale or null if not found
 */
const getPlatformLocale = async () => {
    try {
        const { data: platform } = await $fetch<any>('/api/platform/current');

        return platform?.locale;
    } catch (error) {
        return null;
    }
};

/**
 * Get the update priority for the given updater
 *
 * @param {string | null | undefined} updater
 * @returns {number}
 */
const getUpdatePriority = (updater: string | null | undefined) => {
    if (!updater || !Object.keys(LOCALE_UPDATE_PRIORITIES).includes(updater)) {
        return 0;
    }

    const key = updater as keyof typeof LOCALE_UPDATE_PRIORITIES;

    return LOCALE_UPDATE_PRIORITIES[key];
};

const normalizeLocale = (locale: string): string =>
    locale.split('-')[0].toLowerCase() ?? '';

/**
 * Middleware to check if the user has access to the page
 * @param {Route} to
 */
export default defineNuxtRouteMiddleware(async (to, _from) => {
    const nuxtApp = useNuxtApp();
    const i18n = nuxtApp.$i18n;
    const currentLocale = normalizeLocale(i18n.locale.value);

    // Skip on server
    if (import.meta.server) return;

    const i18nUpdatedByCookie = useCookie('i18n.updatedByCookie');
    const currentCookiePriority = getUpdatePriority(i18nUpdatedByCookie.value);

    const { user } = useUserSession();

    // TODO - optimize this to avoid multiple calls/fetches
    const [updatedBy, rawLocale] =
        Object.entries({
            user: user.value?.locale,
            route: to.query.locale,
            platform: await getPlatformLocale(),
        }).find(([_, fn]) => !!fn) || [];

    if (!rawLocale || !updatedBy) return;

    const updaterPriority = getUpdatePriority(updatedBy);
    if (updaterPriority < currentCookiePriority) return;

    const targetLocale = normalizeLocale(rawLocale);

    // 🛡️ Prevent redundant switching
    if (targetLocale === currentLocale) {
        console.log(
            `[i18n] Locale already "${currentLocale}", skipping switch`,
        );
        return;
    }

    if (!targetLocale) {
        console.warn(
            `[i18n] No valid locale found for updater "${updatedBy}", skipping switch`,
        );
        return;
    }

    if (i18n.strategy === 'no_prefix') {
        console.log(`[i18n] Switching to new locale "${targetLocale}"`);
        await i18n.setLocale(targetLocale);
    } else {
        const path = nuxtApp.$switchLocalePath(targetLocale);
        await navigateTo(path);
    }

    i18nUpdatedByCookie.value = updatedBy;
});
